import createClient from "openapi-fetch";
import {
  getSessionToken,
  invalidateAuthenticatedSession,
} from "../auth/session";
import appConfig from "../configs/app.config";
import { REQUEST_HEADER_AUTH_KEY, TOKEN_TYPE } from "../constants/api.constant";
import type { QuestionnaireImageMode } from "../domain/questionnaire-image-mode";
import type { ApiResult } from "./ApiResult";
import type { components, paths } from "./generated/survey-svc";

export type SurveyError =
  | components["responses"]["badRequest"]["content"]["application/json"]
  | components["responses"]["forbiddenError"]["content"]["application/json"]
  | components["responses"]["notFoundError"]["content"]["application/json"]
  | components["responses"]["unauthorizedError"]["content"]["application/json"]
  | components["responses"]["serverError"]["content"]["application/json"]
  | components["responses"]["agentSessionBadRequest"]["content"]["application/json"]
  | components["responses"]["agentSessionNotFound"]["content"]["application/json"]
  | components["responses"]["agentSessionServerError"]["content"]["application/json"];

const AUTH_ME_TIMEOUT_MS = 8000;

const executeSurveyFetch = async (
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  authenticated: boolean,
) => {
  const headers = new Headers(
    input instanceof Request ? input.headers : init?.headers,
  );
  const url = input instanceof Request ? input.url : input.toString();
  const accessToken = authenticated ? getSessionToken() : null;

  if (accessToken) {
    headers.set(REQUEST_HEADER_AUTH_KEY, `${TOKEN_TYPE}${accessToken}`);
  }
  const isJsonMutation =
    input instanceof Request &&
    ["POST", "PUT", "PATCH"].includes(input.method) &&
    !input.headers.get("Content-Type")?.startsWith("multipart/form-data");
  if (isJsonMutation || (init?.body && !headers.has("Content-Type"))) {
    headers.set("Content-Type", "application/json");
  }

  const isAuthMe = /\/auth\/me(?:\?|$)/.test(url);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let signal = init?.signal as AbortSignal | undefined;
  if (isAuthMe && !signal) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(
      () => controller.abort(new DOMException("Timeout", "TimeoutError")),
      AUTH_ME_TIMEOUT_MS,
    );
  }

  try {
    const response = await fetch(input, { ...init, headers, signal });
    if (response.status === 401 && accessToken)
      invalidateAuthenticatedSession(accessToken);
    return response;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const surveyFetch: typeof fetch = (input, init) =>
  executeSurveyFetch(input, init, true);

export const unauthenticatedSurveyFetch: typeof fetch = (input, init) =>
  executeSurveyFetch(input, init, false);

const client = createClient<paths>({
  baseUrl: appConfig.indicafixSurveySvcUrl,
  fetch: surveyFetch,
});

const unauthenticatedClient = createClient<paths>({
  baseUrl: appConfig.indicafixSurveySvcUrl,
  fetch: unauthenticatedSurveyFetch,
});

const isImageResponse = (value: unknown): value is { url: string } =>
  typeof value === "object" &&
  value !== null &&
  "url" in value &&
  typeof value.url === "string";

const getDownloadFilename = (contentDisposition: string | null): string => {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  return (
    contentDisposition?.match(/filename="([^"]+)"/i)?.[1] ||
    contentDisposition?.match(/filename=([^;]+)/i)?.[1]?.trim() ||
    "responses.csv"
  );
};

const toResult = async <TData>(
  request: Promise<{ data?: TData; error?: SurveyError; response: Response }>,
  fallbackError?: SurveyError,
): Promise<ApiResult<TData | undefined, SurveyError>> => {
  try {
    const result = await request;
    if (result.response.ok) {
      return {
        status: "success",
        data: result.data,
        statusCode: result.response.status,
      };
    }

    const responseError: unknown = result.error;
    const shouldUseFallbackError =
      fallbackError !== undefined &&
      ((typeof responseError === "string" &&
        responseError.trim().length === 0) ||
        (typeof responseError === "object" &&
          responseError !== null &&
          "error" in responseError &&
          typeof responseError.error === "string" &&
          responseError.error.trim().length === 0));

    return {
      status: "http-error",
      statusCode: result.response.status,
      error: shouldUseFallbackError
        ? fallbackError
        : (result.error ??
          fallbackError ?? { error: result.response.statusText }),
    };
  } catch (error: unknown) {
    return { status: "network-error", error };
  }
};

export const surveyClient = {
  getProjectReport(projectId: string) {
    return toResult(
      client.GET("/projects/{projectId}/report", {
        params: { path: { projectId } },
        cache: "no-store",
      }),
    );
  },
  publishProjectReport(projectId: string, file: File) {
    const body = new FormData();
    body.append("file", file);
    return toResult(
      client.POST("/projects/{projectId}/report", {
        params: { path: { projectId } },
        body: { file: file.name },
        bodySerializer: () => body,
      }),
    );
  },
  replaceProjectReport(projectId: string, file: File) {
    const body = new FormData();
    body.append("file", file);
    return toResult(
      client.PUT("/projects/{projectId}/report", {
        params: { path: { projectId } },
        body: { file: file.name },
        bodySerializer: () => body,
      }),
    );
  },
  removeProjectReport(projectId: string) {
    return toResult(
      client.DELETE("/projects/{projectId}/report", {
        params: { path: { projectId } },
      }),
    );
  },
  downloadProjectReport(projectId: string) {
    return toResult(
      client.GET("/projects/{projectId}/report/content", {
        params: { path: { projectId } },
        parseAs: "blob",
        cache: "no-store",
      }),
    );
  },
  getDashboard(
    surveyId: string,
    roundId: string,
    filters?: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/round/{roundId}", {
        params: {
          path: { surveyId, roundId },
          query: filters ? { filters } : undefined,
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  getDashboardSummary(surveyId: string, roundId: string, language?: string) {
    return toResult(
      client.GET("/dashboard/{surveyId}/round/{roundId}/summary", {
        params: {
          path: { surveyId, roundId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  getFakeDashboard(surveyId: string, filters?: string) {
    return toResult(
      client.GET("/dashboard/{surveyId}/fake", {
        params: {
          path: { surveyId },
          query: filters ? { filters } : undefined,
        },
      }),
    );
  },
  getFakeTemplateDashboard(templateId: string, filters?: string) {
    return toResult(
      client.GET("/dashboard/template/{templateId}/fake", {
        params: {
          path: { templateId },
          query: filters ? { filters } : undefined,
        },
      }),
    );
  },
  getOtherDashboardAnswers(
    surveyId: string,
    questionId: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/{questionId}/other-summary", {
        params: {
          path: { surveyId, questionId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  getTextDashboardAnswers(
    surveyId: string,
    questionId: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/{questionId}/text-responses", {
        params: {
          path: { surveyId, questionId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  exportOtherDashboardAnswers(
    surveyId: string,
    questionId: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/{questionId}/other-summary/export", {
        params: {
          path: { surveyId, questionId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  exportTextDashboardAnswers(
    surveyId: string,
    questionId: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/{questionId}/text-responses/export", {
        params: {
          path: { surveyId, questionId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  exportDashboard(
    surveyId: string,
    roundId: string,
    filters?: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/dashboard/{surveyId}/round/{roundId}/export-dashboard", {
        params: {
          path: { surveyId, roundId },
          query: filters ? { filters } : undefined,
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  exportSurveyAnswers(surveyId: string) {
    return toResult(
      client.GET("/answers/{surveyId}/export-answers", {
        params: { path: { surveyId } },
      }),
    );
  },
  async downloadSurveyResponses(
    token: string,
  ): Promise<ApiResult<{ blob: Blob; filename: string }, SurveyError>> {
    try {
      const result = await unauthenticatedClient.GET(
        "/answers/download/{token}.csv",
        {
          params: { path: { token } },
          parseAs: "blob",
        },
      );
      if (!result.response.ok) {
        return {
          status: "http-error",
          statusCode: result.response.status,
          error: result.error ?? { error: result.response.statusText },
        };
      }
      if (result.data === undefined) {
        return {
          status: "network-error",
          error: new Error("Missing response download body"),
        };
      }
      return {
        status: "success",
        statusCode: result.response.status,
        data: {
          blob: result.data,
          filename: getDownloadFilename(
            result.response.headers.get("content-disposition"),
          ),
        },
      };
    } catch (error: unknown) {
      return { status: "network-error", error };
    }
  },
  getSlaDashboard(
    parameters: NonNullable<
      paths["/sla/dashboard"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/sla/dashboard", { params: { query: parameters } }),
    );
  },
  getSla(companyId?: string) {
    return toResult(
      client.GET("/sla", {
        params: { query: companyId ? { companyId } : undefined },
      }),
    );
  },
  getSlaSubmissions(companyId: string, filters?: string) {
    return toResult(
      client.GET("/sla/submissions", {
        params: {
          query: {
            ...(companyId ? { companyId } : {}),
            ...(filters ? { filters } : {}),
          },
        },
      }),
    );
  },
  getSlaSubmission(id: string, companyId: string) {
    return toResult(
      client.GET("/sla/submissions/{id}", {
        params: { path: { id }, query: companyId ? { companyId } : undefined },
      }),
    );
  },
  updateSlaSubmission(
    id: string,
    companyId: string,
    body: components["schemas"]["SlaUpdateSubmissionParams"],
  ) {
    return toResult(
      client.PATCH("/sla/submissions/{id}", {
        params: { path: { id }, query: companyId ? { companyId } : undefined },
        body,
      }),
    );
  },
  deleteSlaSubmission(id: string, companyId: string) {
    return toResult(
      client.DELETE("/sla/submissions/{id}", {
        params: { path: { id }, query: companyId ? { companyId } : undefined },
      }),
    );
  },
  updateSlaAnswerTags(
    companyId: string,
    body: components["schemas"]["SlaAnswerTagsParams"],
  ) {
    return toResult(
      client.PATCH("/sla/answer-tags", {
        params: { query: companyId ? { companyId } : undefined },
        body,
      }),
    );
  },
  getProjectSla(companyId: string, projectId: string) {
    return toResult(
      client.GET("/projects/{companyId}/{projectId}/sla", {
        params: { path: { companyId, projectId } },
      }),
    );
  },
  createProjectSla(
    companyId: string,
    projectId: string,
    body: components["schemas"]["ProjectSlaParams"],
  ) {
    return toResult(
      client.POST("/projects/{companyId}/{projectId}/sla", {
        params: { path: { companyId, projectId } },
        body,
      }),
    );
  },
  updateProjectSla(
    companyId: string,
    projectId: string,
    body: components["schemas"]["ProjectSlaParams"],
  ) {
    return toResult(
      client.PUT("/projects/{companyId}/{projectId}/sla", {
        params: { path: { companyId, projectId } },
        body,
      }),
    );
  },
  archiveProjectSla(companyId: string, projectId: string) {
    return toResult(
      client.DELETE("/projects/{companyId}/{projectId}/sla", {
        params: { path: { companyId, projectId } },
      }),
    );
  },
  updateProjectSlaTag(
    companyId: string,
    projectId: string,
    body: components["schemas"]["ProjectSlaTagParams"],
  ) {
    return toResult(
      client.PATCH("/projects/{companyId}/{projectId}/sla/tag", {
        params: { path: { companyId, projectId } },
        body,
      }),
    );
  },
  signIn(body: components["schemas"]["AuthSigninParams"]) {
    return toResult(unauthenticatedClient.POST("/auth/signin", { body }));
  },
  requestEmailCode(body: components["schemas"]["AuthEmailCodeRequestParams"]) {
    return toResult(
      unauthenticatedClient.POST("/auth/email-code/request", { body }),
    );
  },
  verifyEmailCode(body: components["schemas"]["AuthEmailCodeVerifyParams"]) {
    return toResult(
      unauthenticatedClient.POST("/auth/email-code/verify", { body }),
    );
  },
  resendEmailCode(body: components["schemas"]["AuthEmailCodeResendParams"]) {
    return toResult(
      unauthenticatedClient.POST("/auth/email-code/resend", { body }),
    );
  },
  twoFactorAuthentication(
    body: components["schemas"]["AuthTwoFactorAuthenticationParams"],
  ) {
    return toResult(
      unauthenticatedClient.POST("/auth/two-factor-authentication", { body }),
    );
  },
  completeProviderAuthentication(
    body: components["schemas"]["AuthProviderCompletionParams"],
  ) {
    return toResult(
      unauthenticatedClient.POST("/auth/providers/completions", { body }),
    );
  },
  completeProviderRegistration(
    body: components["schemas"]["AuthProviderRegistrationParams"],
  ) {
    return toResult(client.POST("/auth/providers/registrations", { body }));
  },
  signUp(body: components["schemas"]["AuthSignupParams"]) {
    return toResult(unauthenticatedClient.POST("/auth/signup", { body }));
  },
  signOut() {
    return toResult(client.DELETE("/auth/signout"));
  },
  forgotPassword(body: components["schemas"]["AuthForgotPasswordParams"]) {
    return toResult(
      unauthenticatedClient.POST("/auth/forgot-password", { body }),
    );
  },
  resetPassword(body: components["schemas"]["AuthResetPasswordParams"]) {
    return toResult(
      unauthenticatedClient.POST("/auth/reset-password", { body }),
    );
  },
  resetUserPassword(
    body: components["schemas"]["AuthResetUserPasswordParams"],
  ) {
    return toResult(client.POST("/auth/reset-user-password", { body }));
  },
  currentUser() {
    return toResult(client.GET("/auth/me"));
  },
  getExternalIdentities() {
    return toResult(client.GET("/auth/external-identities"));
  },
  removeExternalIdentity(identityId: string) {
    return toResult(
      client.DELETE("/auth/external-identities/{identityId}", {
        params: { path: { identityId } },
      }),
    );
  },
  updateProfile(body: components["schemas"]["CustomerUpdateProfileParams"]) {
    return toResult(client.PUT("/customers", { body }));
  },
  uploadProfileImage(file: File) {
    const body = new FormData();
    body.append("file", file);
    return (async (): Promise<
      ApiResult<components["schemas"]["CustomerProfileImage"], SurveyError>
    > => {
      try {
        const response = await surveyFetch(
          new Request(
            `${appConfig.indicafixSurveySvcUrl}/customers/profileimg`,
            {
              method: "POST",
              body,
            },
          ),
        );
        if (!response.ok) {
          const body: unknown = await response.json().catch(() => undefined);
          const error =
            typeof body === "object" &&
            body !== null &&
            "error" in body &&
            typeof body.error === "string"
              ? body.error
              : response.statusText;
          return {
            status: "http-error",
            statusCode: response.status,
            error: { error },
          };
        }
        const data: unknown = await response.json();
        return isImageResponse(data)
          ? { status: "success", data, statusCode: response.status }
          : {
              status: "network-error",
              error: new Error("Invalid profile image response"),
            };
      } catch (error: unknown) {
        return { status: "network-error", error };
      }
    })();
  },
  removeProfileImage() {
    return toResult(client.DELETE("/customers/profileimg"));
  },
  getCompany() {
    return toResult(client.GET("/companies"));
  },
  updateCompany(body: components["schemas"]["CompanyUpdateParams"]) {
    return toResult(client.PUT("/companies", { body }));
  },
  listAdminCompanies(
    parameters: NonNullable<
      paths["/companies/all"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/companies/all", { params: { query: parameters } }),
    );
  },
  getAdminCompany(id: string) {
    return toResult(
      client.GET("/companies/{id}/details", { params: { path: { id } } }),
    );
  },
  updateAdminCompany(
    id: string,
    body: components["schemas"]["AdminCompanyUpdateParams"],
  ) {
    return toResult(
      client.PUT("/companies/{id}/details", {
        params: { path: { id } },
        body,
      }),
    );
  },
  exportAdminCompanies(
    parameters: NonNullable<
      paths["/companies/all/downloads"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/companies/all/downloads", {
        params: { query: parameters },
      }),
    );
  },
  listAdminCustomers(
    parameters: NonNullable<
      paths["/customers/all"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/customers/all", { params: { query: parameters } }),
    );
  },
  getAdminCustomer(id: string) {
    return toResult(
      client.GET("/customers/{id}/details", { params: { path: { id } } }),
    );
  },
  updateAdminCustomer(
    id: string,
    body: components["schemas"]["AdminCustomerUpdateParams"],
  ) {
    return toResult(
      client.PUT("/customers/{id}/details", {
        params: { path: { id } },
        body,
      }),
    );
  },
  exportAdminCustomers(
    parameters: NonNullable<
      paths["/customers/all/downloads"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/customers/all/downloads", {
        params: { query: parameters },
      }),
    );
  },
  listAdminProjects(
    companyId: string,
    parameters: NonNullable<
      paths["/projects/{companyId}/all-projects"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/projects/{companyId}/all-projects", {
        params: { path: { companyId }, query: parameters },
      }),
    );
  },
  getAdminProject(companyId: string, projectId: string, language?: string) {
    return toResult(
      client.GET("/projects/{companyId}/{projectId}/details", {
        params: {
          path: { companyId, projectId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  getAdminProjectSummary(companyId: string) {
    return toResult(
      client.GET("/projects/{companyId}/projects-summary", {
        params: { path: { companyId } },
      }),
    );
  },
  createAdminProject(
    companyId: string,
    body: components["schemas"]["AdminProjectCreateParams"],
  ) {
    return toResult(
      client.POST("/projects/{companyId}", {
        params: { path: { companyId } },
        body,
      }),
    );
  },
  cloneAdminProject(companyId: string, projectId: string) {
    return toResult(
      client.POST("/projects/{companyId}/{projectId}/clone", {
        params: { path: { companyId, projectId } },
      }),
    );
  },
  deleteAdminProject(companyId: string, projectId: string) {
    return toResult(
      client.DELETE("/projects/{companyId}/{projectId}", {
        params: { path: { companyId, projectId } },
      }),
    );
  },
  completeCompanyOnboarding(
    body: components["schemas"]["OnboardingCompanyParams"],
  ) {
    return toResult(client.POST("/onboarding/company", { body }), {});
  },
  completeTopicsOnboarding(
    body: components["schemas"]["OnboardingTopicsParams"],
  ) {
    return toResult(client.POST("/onboarding/topics", { body }), {});
  },
  listProjects(
    parameters: NonNullable<
      paths["/projects"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(client.GET("/projects", { params: { query: parameters } }));
  },
  createProject(body: components["schemas"]["ProjectCreateParams"]) {
    return toResult(client.POST("/projects", { body }));
  },
  getProject(id: string, language?: string) {
    return toResult(
      client.GET("/projects/{id}", {
        params: {
          path: { id },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  updateProject(
    id: string,
    body: components["schemas"]["ProjectUpdateParams"],
  ) {
    return toResult(
      client.PUT("/projects/{id}", { params: { path: { id } }, body }),
    );
  },
  updateAdminProject(
    companyId: string,
    projectId: string,
    body: components["schemas"]["ProjectUpdateParams"],
  ) {
    return toResult(
      client.PUT("/projects/{companyId}/{projectId}/details", {
        params: { path: { companyId, projectId } },
        body,
      }),
    );
  },
  updateProjectDashboardPalette(
    projectId: string,
    body: components["schemas"]["DashboardPalette"],
  ) {
    return toResult(
      client.PUT("/projects/{projectId}/dashboard-palette", {
        params: { path: { projectId } },
        body,
      }),
    );
  },
  deleteProjectDashboardPalette(projectId: string) {
    return toResult(
      client.DELETE("/projects/{projectId}/dashboard-palette", {
        params: { path: { projectId } },
      }),
    );
  },
  cloneProject(projectId: string) {
    return toResult(
      client.POST("/projects/{projectId}/clone", {
        params: { path: { projectId } },
      }),
    );
  },
  deleteProject(id: string) {
    return toResult(
      client.DELETE("/projects/{id}", { params: { path: { id } } }),
    );
  },
  getProjectAgentSession(projectId: string) {
    return toResult(
      client.GET("/projects/{projectId}/agent-session", {
        params: { path: { projectId } },
      }),
    );
  },
  saveProjectAgentSession(
    projectId: string,
    body: components["schemas"]["ProjectAgentSessionRequest"],
  ) {
    return toResult(
      client.PUT("/projects/{projectId}/agent-session", {
        params: { path: { projectId } },
        body,
      }),
    );
  },
  uploadCompanyImage(file: File) {
    const body = new FormData();
    body.append("file", file);
    return (async (): Promise<
      ApiResult<components["schemas"]["CompanyImage"], SurveyError>
    > => {
      try {
        const response = await surveyFetch(
          new Request(`${appConfig.indicafixSurveySvcUrl}/companies/logo`, {
            method: "POST",
            body,
          }),
        );
        if (!response.ok) {
          const responseBody: unknown = await response
            .json()
            .catch(() => undefined);
          const error =
            typeof responseBody === "object" &&
            responseBody !== null &&
            "error" in responseBody &&
            typeof responseBody.error === "string"
              ? responseBody.error
              : response.statusText;
          return {
            status: "http-error",
            statusCode: response.status,
            error: { error },
          };
        }
        const data: unknown = await response.json();
        return isImageResponse(data)
          ? { status: "success", data, statusCode: response.status }
          : {
              status: "network-error",
              error: new Error("Invalid company image response"),
            };
      } catch (error: unknown) {
        return { status: "network-error", error };
      }
    })();
  },
  removeCompanyImage() {
    return toResult(client.DELETE("/companies/logo"));
  },
  getPreviewAccess(surveyId: string, signal: AbortSignal) {
    return toResult(
      client.GET("/questionnaires/{surveyId}/preview-access", {
        params: { path: { surveyId } },
        cache: "no-store",
        signal,
      }),
    );
  },
  getActiveQuestionnaire(surveyId: string, language?: string) {
    return toResult(
      client.GET("/questionnaires/{surveyId}/active", {
        params: {
          path: { surveyId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  getCompanyActiveQuestionnaire(
    companyId: string,
    surveyId: string,
    language?: string,
  ) {
    return toResult(
      client.GET("/questionnaires/{companyId}/{surveyId}/active", {
        params: {
          path: { companyId, surveyId },
          header: language ? { language } : undefined,
        },
      }),
    );
  },
  updateQuestionnaire(
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireUpdateQuestionnaireParams"],
    language?: string,
  ) {
    return toResult(
      client.PATCH("/questionnaires/{questionnaireId}", {
        params: {
          path: { questionnaireId },
          header: language ? { language } : undefined,
        },
        body,
      }),
    );
  },
  updateCompanyQuestionnaire(
    companyId: string,
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireUpdateQuestionnaireParams"],
    language?: string,
  ) {
    return toResult(
      client.PATCH("/questionnaires/{companyId}/{questionnaireId}", {
        params: {
          path: { companyId, questionnaireId },
          header: language ? { language } : undefined,
        },
        body,
      }),
    );
  },
  updateQuestionnaireStatus(
    questionnaireId: string,
    status: components["schemas"]["QuestionnaireUpdateQuestionnaireStatusParams"]["status"],
  ) {
    return toResult(
      client.PATCH("/questionnaires/{questionnaireId}/status", {
        params: { path: { questionnaireId } },
        body: { status },
      }),
    );
  },
  updateQuestionnaireOrder(
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireUpdateQuestionnaireOrderParams"],
  ) {
    return toResult(
      client.PATCH("/questionnaires/{questionnaireId}/reorder", {
        params: { path: { questionnaireId } },
        body,
      }),
    );
  },
  updateQuestionnaireDashboardOrder(
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireUpdateQuestionnaireDashboardOrderParams"],
  ) {
    return toResult(
      client.PATCH("/questionnaires/{questionnaireId}/reorder/dash", {
        params: { path: { questionnaireId } },
        body,
      }),
    );
  },
  uploadQuestionnaireLogo(
    questionnaireId: string,
    file: File,
    imageMode?: QuestionnaireImageMode,
  ) {
    const body = new FormData();
    body.append("file", file);
    if (imageMode) body.append("imageMode", imageMode);
    return toResult(
      client.POST("/questionnaires/{questionnaireId}/logo", {
        params: { path: { questionnaireId } },
        body: {
          file: file.name,
          ...(imageMode ? { imageMode } : {}),
        },
        bodySerializer: () => body,
      }),
    );
  },
  removeQuestionnaireLogo(questionnaireId: string) {
    return toResult(
      client.DELETE("/questionnaires/{questionnaireId}/logo", {
        params: { path: { questionnaireId } },
      }),
    );
  },
  addQuestionToQuestionnaire(
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireAddQuestionToQuestionnaireParams"],
  ) {
    return toResult(
      client.POST("/questionnaires/{questionnaireId}/questions", {
        params: { path: { questionnaireId } },
        body,
      }),
    );
  },
  addQuestionsFromLibraryToQuestionnaire(
    questionnaireId: string,
    body: components["schemas"]["QuestionnaireAddQuestionsFromLibraryParams"],
  ) {
    return toResult(
      client.POST("/questionnaires/{questionnaireId}/questions/database", {
        params: { path: { questionnaireId } },
        body,
      }),
    );
  },
  updateQuestionOfQuestionnaire(
    questionnaireId: string,
    questionId: string,
    body: components["schemas"]["QuestionnaireUpdateQuestionOfQuestionnaireParams"],
    language?: string,
  ) {
    return toResult(
      client.PATCH("/questionnaires/{questionnaireId}/questions/{questionId}", {
        params: {
          path: { questionnaireId, questionId },
          header: language ? { language } : undefined,
        },
        body,
      }),
    );
  },
  deleteQuestionFromQuestionnaire(questionnaireId: string, questionId: string) {
    return toResult(
      client.DELETE(
        "/questionnaires/{questionnaireId}/questions/{questionId}",
        { params: { path: { questionnaireId, questionId } } },
      ),
    );
  },
  listThemes() {
    return toResult(client.GET("/themes"));
  },
  getTheme(id: string) {
    return toResult(client.GET("/themes/{id}", { params: { path: { id } } }));
  },
  createTheme(body: components["schemas"]["ThemeAddParams"]) {
    return toResult(client.POST("/themes", { body }));
  },
  updateTheme(id: string, body: components["schemas"]["ThemeUpdateParams"]) {
    return toResult(
      client.PUT("/themes/{id}", { params: { path: { id } }, body }),
    );
  },
  deleteTheme(id: string) {
    return toResult(
      client.DELETE("/themes/{id}", { params: { path: { id } } }),
    );
  },
  updateThemeOrder(
    body: components["schemas"]["ThemeUpdateThemesOrderParams"],
  ) {
    return toResult(client.PATCH("/themes/order", { body }));
  },
  addTemplatesToTheme(
    id: string,
    body: components["schemas"]["ThemeAddTemplatesParams"],
  ) {
    return toResult(
      client.PATCH("/themes/{id}/templates", {
        params: { path: { id } },
        body,
      }),
    );
  },
  listTemplates(
    parameters: NonNullable<
      paths["/templates"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/templates", { params: { query: parameters } }),
    );
  },
  getTemplate(id: string) {
    return toResult(
      client.GET("/templates/{id}", { params: { path: { id } } }),
    );
  },
  createTemplate(body: components["schemas"]["TemplateAddParams"]) {
    return toResult(client.POST("/templates", { body }));
  },
  updateTemplate(
    id: string,
    body: components["schemas"]["TemplateUpdateParams"],
  ) {
    return toResult(
      client.PUT("/templates/{id}", { params: { path: { id } }, body }),
    );
  },
  deleteTemplate(id: string) {
    return toResult(
      client.DELETE("/templates/{id}", { params: { path: { id } } }),
    );
  },
  addQuestionsToTemplate(
    id: string,
    body: components["schemas"]["TemplateAddQuestionsParams"],
  ) {
    return toResult(
      client.PATCH("/templates/{id}/questions", {
        params: { path: { id } },
        body,
      }),
    );
  },
  addIndicatorsToTemplate(
    id: string,
    body: components["schemas"]["TemplateAddIndicatorsParams"],
  ) {
    return toResult(
      client.PATCH("/templates/{id}/indicators", {
        params: { path: { id } },
        body,
      }),
    );
  },
  updateTemplateDashboardOrder(
    id: string,
    body: components["schemas"]["TemplateUpdateDashboardOrderParams"],
  ) {
    return toResult(
      client.PATCH("/templates/{id}/dashboard-order", {
        params: { path: { id } },
        body,
      }),
    );
  },
  removeQuestionFromTemplate(templateId: string, questionId: string) {
    return toResult(
      client.DELETE("/templates/{templateId}/questions/{questionId}", {
        params: { path: { templateId, questionId } },
      }),
    );
  },
  createProjectFromTemplate(
    templateId: string,
    body: components["schemas"]["TemplateUseParams"],
  ) {
    return toResult(
      client.POST("/templates/{id}/use", {
        params: { path: { id: templateId } },
        body,
      }),
    );
  },
  createCompanyProjectFromTemplate(
    companyId: string,
    templateId: string,
    body: components["schemas"]["TemplateUseParams"],
  ) {
    return toResult(
      client.POST("/templates/{companyId}/{templateId}/use", {
        params: { path: { companyId, templateId } },
        body,
      }),
    );
  },
  uploadThemeImage(id: string, file: File) {
    const body = new FormData();
    body.append("file", file);
    return toResult(
      client.POST("/themes/{id}/image", {
        params: { path: { id } },
        body: body as unknown as { file: string },
      }),
    );
  },
  listQuestions(
    parameters: NonNullable<
      paths["/questions"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/questions", { params: { query: parameters } }),
    );
  },
  getQuestion(id: string) {
    return toResult(
      client.GET("/questions/{id}", { params: { path: { id } } }),
    );
  },
  createQuestion(body: components["schemas"]["QuestionAddQuestionParams"]) {
    return toResult(client.POST("/questions", { body }));
  },
  updateQuestion(
    id: string,
    body: components["schemas"]["QuestionUpdateQuestionParams"],
  ) {
    return toResult(
      client.PUT("/questions/{id}", { params: { path: { id } }, body }),
    );
  },
  deleteQuestion(id: string) {
    return toResult(
      client.DELETE("/questions/{id}", { params: { path: { id } } }),
    );
  },
  exportQuestions(
    parameters: NonNullable<
      paths["/questions/all/downloads"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/questions/all/downloads", { params: { query: parameters } }),
    );
  },
  listSurveys(
    parameters: NonNullable<
      paths["/surveys"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(client.GET("/surveys", { params: { query: parameters } }));
  },
  createSurvey() {
    return toResult(client.POST("/surveys"));
  },
  getSurvey(id: string) {
    return toResult(client.GET("/surveys/{id}", { params: { path: { id } } }));
  },
  updateSurvey(
    id: string,
    body: components["schemas"]["SurveyUpdateSurveyParams"],
  ) {
    return toResult(
      client.PUT("/surveys/{id}", { params: { path: { id } }, body }),
    );
  },
  deleteSurvey(id: string) {
    return toResult(
      client.DELETE("/surveys/{id}", { params: { path: { id } } }),
    );
  },
  createRound(
    surveyId: string,
    body?: components["schemas"]["SurveyAddRoundParams"],
  ) {
    return toResult(
      client.POST("/surveys/{surveyId}/rounds", {
        params: { path: { surveyId } },
        body,
      }),
    );
  },
  updateRoundStatus(
    surveyId: string,
    roundId: string,
    status: components["schemas"]["SurveyUpdateRoundStatusParams"]["status"],
  ) {
    return toResult(
      client.PATCH("/surveys/{surveyId}/rounds/{roundId}/status", {
        params: { path: { surveyId, roundId } },
        body: { status },
      }),
    );
  },
  updateRoundDates(
    surveyId: string,
    roundId: string,
    body: components["schemas"]["SurveyAddDateInRoundParams"],
  ) {
    return toResult(
      client.PATCH("/surveys/{surveyId}/rounds/{roundId}/dates", {
        params: { path: { surveyId, roundId } },
        body,
      }),
    );
  },
  updateRoundDashboardUrl(
    surveyId: string,
    roundId: string,
    body: components["schemas"]["SurveyAddDashBoardUrlInRoundParams"],
  ) {
    return toResult(
      client.PATCH("/surveys/{surveyId}/rounds/{roundId}/dashboardurl", {
        params: { path: { surveyId, roundId } },
        body,
      }),
    );
  },
  listCompanySurveys(
    companyId: string,
    parameters: NonNullable<
      paths["/surveys/{companyId}/all-surveys"]["get"]["parameters"]["query"]
    > = {},
  ) {
    return toResult(
      client.GET("/surveys/{companyId}/all-surveys", {
        params: { path: { companyId }, query: parameters },
      }),
    );
  },
  getCompanySurveyDetails(companyId: string, surveyId: string) {
    return toResult(
      client.GET("/surveys/{companyId}/{surveyId}/details", {
        params: { path: { companyId, surveyId } },
      }),
    );
  },
  updateCompanySurvey(
    companyId: string,
    surveyId: string,
    body: components["schemas"]["SurveyUpdateSurveyParams"],
  ) {
    return toResult(
      client.PUT("/surveys/{companyId}/{surveyId}", {
        params: { path: { companyId, surveyId } },
        body,
      }),
    );
  },
  deleteCompanySurvey(companyId: string, surveyId: string) {
    return toResult(
      client.DELETE("/surveys/{companyId}/{surveyId}", {
        params: { path: { companyId, surveyId } },
      }),
    );
  },
  updateCompanyRoundStatus(
    companyId: string,
    surveyId: string,
    roundId: string,
    status: components["schemas"]["SurveyUpdateRoundStatusParams"]["status"],
  ) {
    return toResult(
      client.PATCH("/surveys/{companyId}/{surveyId}/rounds/{roundId}/status", {
        params: { path: { companyId, surveyId, roundId } },
        body: { status },
      }),
    );
  },
  updateCompanyRoundDates(
    companyId: string,
    surveyId: string,
    roundId: string,
    body: components["schemas"]["SurveyAddDateInRoundParams"],
  ) {
    return toResult(
      client.PATCH("/surveys/{companyId}/{surveyId}/rounds/{roundId}/dates", {
        params: { path: { companyId, surveyId, roundId } },
        body,
      }),
    );
  },
};
